import { Alert } from '@startuptools/ui/Alert';
import { Box } from '@startuptools/ui/Box';
import { Button } from '@startuptools/ui/Button';
import { Card } from '@startuptools/ui/Card';
import { Chip } from '@startuptools/ui/Chip';
import { Stack } from '@startuptools/ui/Stack';
import { Switch } from '@startuptools/ui/Switch';
import { Tooltip } from '@startuptools/ui/Tooltip';
import { Typography } from '@startuptools/ui/Typography';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { CreateTransaction } from './CreateTransaction';
import { useShareTransactionGroupViewSuspenseQuery } from './transaction-group.graphql';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import { TransactionsTable } from '../components/TransactionsTable';
import { Trans } from '@lingui/react';
import { SnrFilingLink } from '../../../about/components/SnrFilingLink';
import { StgLabel } from '../StgLabel';
import React, { useState } from 'react';
import { NextGroupDialog } from './NextGroup';
import { useCurrentUserSuspenseQuery } from '../../../../graphql/react-operations';
import { GqlTransactionKind } from '../../../../graphql/base-types.graphql';
import { FileUploadInput } from '@startuptools/ui/FileUploadInput';
import { Divider } from '@startuptools/ui/Divider';
import { useStImportTransfersMutation } from './index.graphql';
import ReportIcon from '@mui/icons-material/Report';
import { useNavigate } from '../../../../components/Navigate';
import { Link, useParams } from 'react-router-dom';

export const TransactionGroup = () => {
  const { shareTransactionGroupId: stgId } = useParams();
  const companyId = useCompanyId();
  const { link } = useNavigate();
  const [file, setFile] = useState<File | null>(null);
  const {
    data: { currentUser: user },
  } = useCurrentUserSuspenseQuery();
  const { data } = useShareTransactionGroupViewSuspenseQuery({
    variables: {
      companyId,
      id: stgId!,
    },
    fetchPolicy: 'network-only',
  });
  const scc = data.shareTransactionGroup.snrShareCapitalChange;
  const [showNextGroupDialog, setShowNextGroupDialog] = React.useState(false);
  const [showShareRanges, setShowShareRanges] = React.useState(false);
  const [doImport, { loading: loadingImport }] = useStImportTransfersMutation();

  const handleImportCsv = async () => {
    if (!file) {
      return;
    }
    const content = await file.text();
    return doImport({ variables: { companyId, input: { shareTransactionGroupId: stgId!, csvInput: content } } });
  };

  return (
    <Stack gap={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Button
          variant="outlined"
          startDecorator={<ArrowBackIcon />}
          component={Link}
          to={link('/share-register/transactions')}
        >
          <Trans id="Back" />
        </Button>
        {user.admin && (
          <Switch
            checked={showShareRanges}
            onChange={() => setShowShareRanges(!showShareRanges)}
            endDecorator={
              <Stack direction="row" gap={1}>
                <Trans id="Show Share Ranges" />{' '}
                <Tooltip title={<Trans id="Sys-admin feature only" />}>
                  <Chip size="sm" color="neutral" variant="outlined">
                    admin
                  </Chip>
                </Tooltip>
              </Stack>
            }
          />
        )}
      </Stack>
      <Stack direction="row" gap={2}>
        <Card sx={{ width: '50%' }}>
          {[GqlTransactionKind.Split, GqlTransactionKind.Merge].includes(data.shareTransactionGroup.kind) ? (
            <Stack gap={2}>
              <Alert color="success">
                <Trans
                  id="All shares in this group are assigned. Do you want to continue with the next case from
                  Bolagsverket?"
                />
              </Alert>
              <Box>
                <Button
                  onClick={() => {
                    setShowNextGroupDialog(true);
                  }}
                >
                  <Trans id="Continue" />
                </Button>
              </Box>
            </Stack>
          ) : (
            <>
              <Typography level="h4">
                <Trans id="Add new Transaction" />
              </Typography>
              {scc?.sharesLeftToAllocate === 0 ? (
                <Alert color="success">
                  <Trans id="All shares are allocated. Well done!" />
                </Alert>
              ) : (
                <CreateTransaction
                  company={data.company}
                  stg={data.shareTransactionGroup}
                  onAllSharesAssigned={() => {
                    setShowNextGroupDialog(true);
                  }}
                />
              )}
            </>
          )}
        </Card>

        <Card sx={{ width: '50%' }}>
          <Typography level="h4">
            <StgLabel stg={data.shareTransactionGroup} snrScc={data.shareTransactionGroup.snrShareCapitalChange} />
          </Typography>
          <Stack direction="row" gap={1}>
            <Typography level="body-sm" fontWeight="bold">
              <Trans id="Bolagsverket case:" />
            </Typography>
            {scc ? (
              <SnrFilingLink filingYear={scc.filingYear} filingNumber={scc.filingNumber} />
            ) : (
              <Trans id="No case connected" />
            )}
          </Stack>

          {user.admin && data.shareTransactionGroup.kind === GqlTransactionKind.Transfer && (
            <Stack gap={2}>
              <Divider />
              <Typography
                endDecorator={
                  <Chip size="sm" variant="outlined">
                    admin
                  </Chip>
                }
              >
                <Trans id="Import from CSV" />
              </Typography>
              <Alert startDecorator={<ReportIcon />} variant="outlined" color="danger">
                <Trans id="Ask the Tech team about this functionality before trying to use it" />
              </Alert>
              <FileUploadInput acceptMimeTypes={{ 'text/csv': ['.csv'] }} invalid={false} onChange={f => setFile(f)} />
              <Box>
                <Button disabled={!file} onClick={handleImportCsv} loading={loadingImport}>
                  <Trans id="Import CSV" />
                </Button>
              </Box>
            </Stack>
          )}
        </Card>
      </Stack>

      <Card>
        <TransactionsTable
          transactionGroup={data.shareTransactionGroup}
          showNewTransactionButton={false}
          forbiddenToDeleteStg={false}
          showShareRange={showShareRanges}
        />
      </Card>
      {showNextGroupDialog && <NextGroupDialog skipPrompt={false} onClose={() => setShowNextGroupDialog(false)} />}
    </Stack>
  );
};
